import React, { useState, useEffect } from 'react';
import {
  User,
  Phone,
  Heart,
  AlertTriangle,
  Save,
  Loader2,
  Plus,
  Users,
  Contact,
  MessageCircle,
  Calendar,
  Pill,
  FileText,
  Stethoscope,
  Shield,
  Eye,
  EyeOff
} from 'lucide-react';

import { Button } from './';
import MedicalSelector from './MedicalSelector';
import { commonDiseases, commonAllergies, commonMedications, bloodTypes } from '../../data/medicalData';

// Códigos de país para teléfonos
const countryCodes = [
  { code: '+56', country: 'Chile', flag: '🇨🇱' },
  { code: '+1', country: 'Estados Unidos', flag: '🇺🇸' },
  { code: '+54', country: 'Argentina', flag: '🇦🇷' },
  { code: '+55', country: 'Brasil', flag: '🇧🇷' },
  { code: '+57', country: 'Colombia', flag: '🇨🇴' },
  { code: '+51', country: 'Perú', flag: '🇵🇪' },
  { code: '+598', country: 'Uruguay', flag: '🇺🇾' },
  { code: '+34', country: 'España', flag: '🇪🇸' },
  { code: '+33', country: 'Francia', flag: '🇫🇷' },
  { code: '+49', country: 'Alemania', flag: '🇩🇪' },
  { code: '+39', country: 'Italia', flag: '🇮🇹' },
  { code: '+44', country: 'Reino Unido', flag: '🇬🇧' },
];

// Relaciones familiares/contacto
const relationshipOptions = [
  { value: 'padre', label: 'Padre' },
  { value: 'madre', label: 'Madre' },
  { value: 'hermano', label: 'Hermano/a' },
  { value: 'hijo', label: 'Hijo/a' },
  { value: 'conyuge', label: 'Cónyuge' },
  { value: 'pareja', label: 'Pareja' },
  { value: 'abuelo', label: 'Abuelo/a' },
  { value: 'tio', label: 'Tío/a' },
  { value: 'primo', label: 'Primo/a' },
  { value: 'amigo', label: 'Amigo/a' },
  { value: 'vecino', label: 'Vecino/a' },
  { value: 'medico', label: 'Médico' },
  { value: 'otro', label: 'Otro' },
];

/**
 * @component EmergencyProfileFormUnified
 * @description Formulario unificado para crear y editar perfiles de emergencia
 * Usado tanto en CreateWebIDModal como en EditWebIDModal
 */

export interface EmergencyContact {
  id?: string;
  name: string;
  relationship: string;
  phone: string;
  email?: string;
  isPrimary: boolean;
}

export interface EmergencyProfileFormData {
  name: string;
  phone: string;
  phoneCountryCode: string;
  emergencyContacts: EmergencyContact[];
  bloodType: string;
  diseases: string[];
  allergies: string[];
  medications: string[];
  medicalInfo: string;
  medicalNotes?: string;
  isPublic: boolean;
  showAllergies: boolean;
  showMedicalConditions: boolean;
  showMedications: boolean;
  showMedicalNotes: boolean;
  showEmergencyContacts: boolean;
  allowEmergencyAccess: boolean;
}

interface EmergencyProfileFormUnifiedProps {
  /** Datos iniciales del formulario (para edición) */
  initialData?: Partial<EmergencyProfileFormData>;
  /** Función llamada al enviar el formulario */
  onSubmit: (data: EmergencyProfileFormData) => Promise<void>;
  /** Estado de carga */
  loading?: boolean;
  /** Error a mostrar */
  error?: string | null;
  /** Modo del formulario */
  mode: 'create' | 'edit';
  /** WebID (solo para modo edición) */
  webId?: string;
  /** Función para cancelar */
  onCancel?: () => void;
  /** Mostrar configuración de privacidad */
  showPrivacySettings?: boolean;
  /** Clases CSS adicionales */
  className?: string;
}

const EmergencyProfileFormUnified: React.FC<EmergencyProfileFormUnifiedProps> = ({
  initialData = {},
  onSubmit,
  loading = false,
  error = null,
  mode,
  webId,
  onCancel,
  showPrivacySettings = true,
  className = '',
}) => {
  const [formData, setFormData] = useState<EmergencyProfileFormData>({
    name: '',
    phone: '',
    phoneCountryCode: '+56',
    emergencyContacts: [],
    bloodType: '',
    diseases: [],
    allergies: [],
    medications: [],
    medicalInfo: '',
    medicalNotes: '',
    isPublic: false,
    showAllergies: true,
    showMedicalConditions: true,
    showMedications: true,
    showMedicalNotes: true,
    showEmergencyContacts: true,
    allowEmergencyAccess: true,
    ...initialData,
  });

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Actualizar datos cuando cambian los datos iniciales
  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({
        ...prev,
        ...initialData,
      }));
    }
  }, [initialData]);

  // Estados para el formulario de contactos
  const [showAddContactForm, setShowAddContactForm] = useState(false);
  const [newContact, setNewContact] = useState<EmergencyContact>({
    name: '',
    relationship: '',
    phone: '',
    email: '',
    isPrimary: false,
  });

  // Funciones para manejar contactos de emergencia
  const addContact = () => {
    if (!newContact.name || !newContact.relationship || !newContact.phone) {
      return;
    }

    const contacts = [...formData.emergencyContacts];

    // Verificar límite máximo de 3 contactos
    if (contacts.length >= 3) {
      alert('Solo puedes agregar hasta 3 contactos de emergencia');
      return;
    }

    // Si es el primer contacto o se marca como primario, hacer primario
    if (contacts.length === 0 || newContact.isPrimary) {
      // Desmarcar otros contactos como primarios
      contacts.forEach(contact => contact.isPrimary = false);
      newContact.isPrimary = true;
    }

    contacts.push({ ...newContact });
    setFormData(prev => ({ ...prev, emergencyContacts: contacts }));

    // Resetear formulario
    setNewContact({
      name: '',
      relationship: '',
      phone: '',
      email: '',
      isPrimary: false,
    });
    setShowAddContactForm(false);
  };

  const removeContact = (index: number) => {
    const contacts = [...formData.emergencyContacts];
    const removedContact = contacts[index];
    contacts.splice(index, 1);

    // Si se eliminó el contacto primario y hay otros contactos, hacer primario al primero
    if (removedContact.isPrimary && contacts.length > 0) {
      contacts[0].isPrimary = true;
    }

    setFormData(prev => ({ ...prev, emergencyContacts: contacts }));
  };

  const setPrimaryContact = (index: number) => {
    const contacts = [...formData.emergencyContacts];
    contacts.forEach((contact, i) => {
      contact.isPrimary = i === index;
    });
    setFormData(prev => ({ ...prev, emergencyContacts: contacts }));
  };

  // Validación del formulario
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = 'El nombre es requerido';
    }

    // Teléfono es opcional
    if (formData.phone.trim() && !/^[\d\s\-\(\)]+$/.test(formData.phone.trim())) {
      errors.phone = 'Formato de teléfono inválido (solo números, espacios, guiones y paréntesis)';
    }

    // Validar que haya al menos un contacto de emergencia
    if (formData.emergencyContacts.length === 0) {
      errors.emergencyContacts = 'Debe agregar al menos un contacto de emergencia';
    }

    // Validar cada contacto de emergencia
    formData.emergencyContacts.forEach((contact, index) => {
      if (!contact.name.trim()) {
        errors[`contact_${index}_name`] = `El nombre del contacto ${index + 1} es requerido`;
      }
      if (!contact.relationship.trim()) {
        errors[`contact_${index}_relationship`] = `La relación del contacto ${index + 1} es requerida`;
      }
      if (!contact.phone.trim()) {
        errors[`contact_${index}_phone`] = `El teléfono del contacto ${index + 1} es requerido`;
      } else if (!/^[\d\s\-\(\)]+$/.test(contact.phone.trim())) {
        errors[`contact_${index}_phone`] = `Formato de teléfono inválido para contacto ${index + 1}`;
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
    } catch (err) {
      console.error('Error submitting form:', err);
    }
  };

  const updateFormData = (field: keyof EmergencyProfileFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // Limpiar error de validación si existe
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className={`space-y-6 ${className}`}>
      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Datos del Portador de la Pulsera */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-lg font-medium text-gray-900 mb-2 flex items-center gap-2">
          <User className="h-5 w-5 text-blue-600" />
          Datos del Portador de la Pulsera
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Esta información corresponde a la persona que llevará la pulsera NFC de emergencia.
        </p>

        <div className="grid grid-cols-1 tablet:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nombre Completo *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => updateFormData('name', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                validationErrors.name ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Juan Pérez"
              disabled={loading}
            />
            {validationErrors.name && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Teléfono del Portador (opcional)
            </label>
            <div className="flex gap-2">
              <select
                value={formData.phoneCountryCode}
                onChange={(e) => updateFormData('phoneCountryCode', e.target.value)}
                className="w-24 px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                disabled={loading}
              >
                {countryCodes.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.flag} {country.code}
                  </option>
                ))}
              </select>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => updateFormData('phone', e.target.value)}
                className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  validationErrors.phone ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="9 1234 5678"
                disabled={loading}
              />
            </div>
            {validationErrors.phone && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.phone}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Opcional para menores de edad o personas que no manejan teléfono propio
            </p>
          </div>
        </div>

        {/* Configuración de privacidad para datos del portador */}
        {showPrivacySettings && (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.showEmergencyContacts}
                onChange={(e) => updateFormData('showEmergencyContacts', e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                disabled={loading}
              />
              <span className="text-sm text-gray-700">
                Hacer información de contacto pública (visible para cualquiera que escanee la pulsera)
              </span>
            </label>
            <p className="text-xs text-gray-500 mt-1">
              Si no está marcado, solo será visible para servicios de emergencia autorizados.
            </p>
          </div>
        )}
      </div>

      {/* Contactos de Emergencia */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <h3 className="text-lg font-medium text-gray-900 mb-2 flex items-center gap-2">
          <Users className="h-5 w-5 text-red-600" />
          Contactos de Emergencia
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Personas a contactar en caso de emergencia. Puedes agregar hasta 3 contactos. Esta información es crítica para los servicios de emergencia.
        </p>

        {/* Lista de contactos existentes */}
        {formData.emergencyContacts.length > 0 && (
          <div className="space-y-3 mb-4">
            {formData.emergencyContacts.map((contact, index) => (
              <div key={index} className="bg-white border border-red-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Contact className="h-4 w-4 text-red-600" />
                      <span className="font-medium text-gray-900">{contact.name}</span>
                      {contact.isPrimary && (
                        <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                          Principal
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      {relationshipOptions.find(r => r.value === contact.relationship)?.label || contact.relationship} • {contact.phone}
                    </p>
                    {contact.email && (
                      <p className="text-sm text-gray-500">{contact.email}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {!contact.isPrimary && (
                      <button
                        type="button"
                        onClick={() => setPrimaryContact(index)}
                        className="text-xs text-red-600 hover:text-red-700 underline"
                        disabled={loading}
                      >
                        Hacer principal
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => removeContact(index)}
                      className="text-red-600 hover:text-red-700"
                      disabled={loading}
                    >
                      <AlertTriangle className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error de validación para contactos */}
        {validationErrors.emergencyContacts && (
          <p className="mb-4 text-sm text-red-600">{validationErrors.emergencyContacts}</p>
        )}

        {/* Botón para agregar contacto */}
        {formData.emergencyContacts.length < 3 && !showAddContactForm && (
          <button
            type="button"
            onClick={() => setShowAddContactForm(true)}
            className="w-full py-2 border-2 border-dashed border-red-300 rounded-lg text-red-600 hover:border-red-400 hover:text-red-700 transition-colors flex items-center justify-center gap-2"
            disabled={loading}
          >
            <Plus className="h-4 w-4" />
            Agregar contacto de emergencia
          </button>
        )}

        {/* Formulario para nuevo contacto */}
        {showAddContactForm && (
          <div className="bg-white border border-red-200 rounded-lg p-4 space-y-4">
            <h4 className="font-medium text-gray-900">Nuevo contacto de emergencia</h4>

            <div className="grid grid-cols-1 tablet:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre Completo *
                </label>
                <input
                  type="text"
                  value={newContact.name}
                  onChange={(e) => setNewContact(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="María González"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Relación/Parentesco *
                </label>
                <select
                  value={newContact.relationship}
                  onChange={(e) => setNewContact(prev => ({ ...prev, relationship: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  disabled={loading}
                >
                  <option value="">Seleccionar relación</option>
                  {relationshipOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Teléfono *
                </label>
                <input
                  type="tel"
                  value={newContact.phone}
                  onChange={(e) => setNewContact(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="9 8765 4321"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email (opcional)
                </label>
                <input
                  type="email"
                  value={newContact.email}
                  onChange={(e) => setNewContact(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="maria@ejemplo.com"
                  disabled={loading}
                />
              </div>
            </div>

            {formData.emergencyContacts.length === 0 && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isPrimary"
                  checked={newContact.isPrimary}
                  onChange={(e) => setNewContact(prev => ({ ...prev, isPrimary: e.target.checked }))}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  disabled={loading}
                />
                <label htmlFor="isPrimary" className="text-sm text-gray-700">
                  Contacto principal
                </label>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                type="button"
                onClick={addContact}
                disabled={loading || !newContact.name || !newContact.relationship || !newContact.phone}
                className="flex-1"
              >
                <Plus className="h-4 w-4 mr-2" />
                Agregar contacto
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowAddContactForm(false);
                  setNewContact({
                    name: '',
                    relationship: '',
                    phone: '',
                    email: '',
                    isPrimary: false,
                  });
                }}
                disabled={loading}
              >
                Cancelar
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Información Médica */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <h3 className="text-lg font-medium text-gray-900 mb-2 flex items-center gap-2">
          <Heart className="h-5 w-5 text-green-600" />
          Información Médica
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Información médica relevante para servicios de emergencia. Todos los campos son opcionales.
        </p>
        
        <div className="space-y-6">
          {/* Tipo de Sangre */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Sangre
            </label>
            <select
              value={formData.bloodType}
              onChange={(e) => updateFormData('bloodType', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              disabled={loading}
            >
              <option value="">Seleccionar tipo de sangre</option>
              {bloodTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Enfermedades o Condiciones Médicas */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">
                Enfermedades o Condiciones Médicas
              </label>
              {showPrivacySettings && (
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.showMedicalConditions}
                    onChange={(e) => updateFormData('showMedicalConditions', e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    disabled={loading}
                  />
                  <Eye className="h-4 w-4 text-gray-500" />
                  <span className="text-xs text-gray-600">Visible públicamente</span>
                </label>
              )}
            </div>
            <MedicalSelector
              options={commonDiseases}
              value={formData.diseases}
              onChange={(diseases) => updateFormData('diseases', diseases)}
              placeholder="Seleccionar enfermedades..."
              multiple={true}
              allowCustom={true}
              customPlaceholder="Especificar otra enfermedad..."
              showCategories={true}
            />
          </div>

          {/* Alergias */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">
                Alergias
              </label>
              {showPrivacySettings && (
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.showAllergies}
                    onChange={(e) => updateFormData('showAllergies', e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    disabled={loading}
                  />
                  <Eye className="h-4 w-4 text-gray-500" />
                  <span className="text-xs text-gray-600">Visible públicamente</span>
                </label>
              )}
            </div>
            <MedicalSelector
              options={commonAllergies}
              value={formData.allergies}
              onChange={(allergies) => updateFormData('allergies', allergies)}
              placeholder="Seleccionar alergias..."
              multiple={true}
              allowCustom={true}
              customPlaceholder="Especificar otra alergia..."
              showCategories={true}
            />
          </div>

          {/* Medicamentos */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">
                Medicamentos Actuales
              </label>
              {showPrivacySettings && (
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.showMedications}
                    onChange={(e) => updateFormData('showMedications', e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    disabled={loading}
                  />
                  <Eye className="h-4 w-4 text-gray-500" />
                  <span className="text-xs text-gray-600">Visible públicamente</span>
                </label>
              )}
            </div>
            <MedicalSelector
              options={commonMedications}
              value={formData.medications}
              onChange={(medications) => updateFormData('medications', medications)}
              placeholder="Seleccionar medicamentos..."
              multiple={true}
              allowCustom={true}
              customPlaceholder="Especificar otro medicamento..."
              showCategories={true}
            />
          </div>

          {/* Notas Médicas Adicionales */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">
                Notas Médicas Adicionales
              </label>
              {showPrivacySettings && (
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.showMedicalNotes}
                    onChange={(e) => updateFormData('showMedicalNotes', e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    disabled={loading}
                  />
                  <Eye className="h-4 w-4 text-gray-500" />
                  <span className="text-xs text-gray-600">Visible públicamente</span>
                </label>
              )}
            </div>
            <textarea
              value={formData.medicalNotes || formData.medicalInfo}
              onChange={(e) => updateFormData('medicalNotes', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Información médica adicional, observaciones especiales, etc."
              disabled={loading}
            />
          </div>
        </div>
        {/* Configuración de privacidad global */}
        {showPrivacySettings && (
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <Shield className="h-4 w-4 text-blue-600" />
              Configuración de Privacidad
            </h4>
            <div className="space-y-3">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.showEmergencyContacts}
                  onChange={(e) => updateFormData('showEmergencyContacts', e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  disabled={loading}
                />
                <span className="text-sm text-gray-700">
                  Mostrar contactos de emergencia públicamente
                </span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.allowEmergencyAccess}
                  onChange={(e) => updateFormData('allowEmergencyAccess', e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  disabled={loading}
                />
                <span className="text-sm text-gray-700">
                  Permitir acceso de emergencia completo a servicios autorizados
                </span>
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              Los controles individuales arriba permiten configurar qué información médica es visible públicamente.
              Los servicios de emergencia autorizados siempre tendrán acceso completo si está habilitado.
            </p>
          </div>
        )}
      </div>

      {/* Configuración de Privacidad */}
      {showPrivacySettings && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Configuración de Privacidad
          </h3>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isPublic"
              checked={formData.isPublic}
              onChange={(e) => updateFormData('isPublic', e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              disabled={loading}
            />
            <label htmlFor="isPublic" className="ml-2 text-sm text-gray-700">
              Hacer perfil público (visible para servicios de emergencia)
            </label>
          </div>
        </div>
      )}

      {/* Footer con botones */}
      <div className="flex flex-col mobile-lg:flex-row justify-end gap-3 pt-6 border-t border-gray-200">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
          >
            Cancelar
          </Button>
        )}
        <Button
          type="submit"
          disabled={loading}
        >
          {loading ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {mode === 'create' ? 'Creando...' : 'Guardando...'}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Save className="h-4 w-4" />
              {mode === 'create' ? 'Crear WebID' : 'Guardar Cambios'}
            </div>
          )}
        </Button>
      </div>
    </form>
  );
};

export default EmergencyProfileFormUnified;
